import type * as Y from "yjs"
import {
	forwardRef,
	memo,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react"
import { useQuery } from "@rocicorp/zero/react"
import { useBlocker, useNavigate } from "@tanstack/react-router"
import {
	IconCreditCardPlusFill18 as CreditCardIcon,
	IconStackPerspectiveFill18 as StackPerspectiveFillIcon,
} from "nucleo-ui-fill-18"
import {
	IconCircleArrowUpOutline18 as CircleArrowUp,
	IconImageMountainOutline18 as ImageBackgroundIcon,
	IconPhotoMinusOutline18 as ImageMinus,
	IconImagePlusOutline18 as ImagePlusIcon,
	IconStackPerspectiveOutline18 as StackPerspectiveIcon,
	IconTrashOutline18 as TrashIcon,
	IconArrowUpFromLineOutline18 as UploadIcon,
} from "nucleo-ui-outline-18"
import { useDebounce } from "react-use"

import type { DocPresenceKey } from "@productlane/lib"
import type { Changelog, ChangelogTranslation } from "@productlane/zero"
import { AI_BUDGET_REACHED_MESSAGE } from "@productlane/lib"
import { CHANGELOG_STATUS_MESSAGES } from "@productlane/lib/src/ai/tools"
import { cn, getClipboardFiles } from "@productlane/lib/src/browser"
import {
	defaultChangelogCoverUrl,
	generatedCoverVersion,
	HIDDEN_CHANGELOG_COVER,
	isChangelogCoverHidden,
} from "@productlane/lib/src/changelog-cover"
import { pro, scale } from "@productlane/lib/src/plans-client"
import { collabDocName } from "@productlane/lib/src/sockets/docs-collab"
import { isChangelogSaveConflict } from "@productlane/lib/src/versions"
import {
	Button,
	ChangelogTagBadge,
	ChangelogTimeline,
	Textarea,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import type { AutosaveSource } from "./changelog-editor-sync"
import type { ChangelogSelection } from "./release-picker"
import type {
	JSONValue,
	ReadonlyJSONObject,
	ReadonlyJSONValue,
} from "@rocicorp/zero"
import type { Content } from "@tiptap/core"
import type { Editor } from "@tiptap/react"
import {
	isRemoteChange,
	rememberSaved,
	seedSharedDoc,
	watchSeedRace,
} from "@/components/editor/collaboration/shared-doc"
import { sharedDocExtensions } from "@/components/editor/collaboration/shared-doc-extensions"
import {
	acceptsOutsideEdit,
	useSharedDoc,
} from "@/components/editor/collaboration/use-shared-doc"
import { DefaultEditor } from "@/components/editor/default-editor"
import {
	cursorColor,
	RemoteCursors,
} from "@/components/editor/extensions/remote-cursors"
import { GenerateChangelogButton } from "@/components/generate-changelog-button"
import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import {
	ALLOWED_FILE_SIZE_IN_MB,
	CHANGELOG_TITLE_MAXIMUM_LENGTH,
} from "@/constants"
import { env } from "@/env"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { isFetchNetworkError } from "@/lib/ignored-sentry-errors"
import { hasLinearConnection } from "@/lib/linear-connection"
import { useRequiredPlans } from "@/lib/plans"
import { processCoverImage } from "@/lib/process-cover-image"
import { getProjectIcon } from "@/lib/projects"
import { putWithRetry } from "@/lib/put-with-retry"
import { useAuthStateData } from "@/lib/states/auth-state"
import { useIsTranslationRunning } from "@/lib/translation-status"
import { useColorSeedLookup } from "@/lib/use-doc-presence"
import { useChangelogTranslations } from "@/lib/use-item-translations"
import { useRole } from "@/lib/useRole"
import { isContentEmpty, shouldCreateAutoVersion } from "@/lib/versioning"
import { mutateOk, useAuthData, useZero, useZeroWorkspace } from "@/lib/zero"
import { useSidebarAiChatState } from "../threads/components/right-bar/ai-chat/sidebar-ai-chat-state"
import { useChangelogContext, useChangelogLanguage } from "./changelog-context"
import {
	ChangelogCover,
	ChangelogCoverVideo,
	coverBlurhashFor,
} from "./changelog-cover"
import {
	COVER_FILE_ACCEPT,
	MAX_COVER_VIDEO_MB,
	captureVideoPoster,
	coverFor,
	isVideoFile,
} from "./changelog-cover-video"
import {
	createAutosaveGate,
	createSaveQueue,
	isSameEditorDoc,
	pickTranslationRowId,
	shouldApplyTranslationToEditor,
} from "./changelog-editor-sync"
import {
	shouldAnnounceGenerationFailure,
	shouldAnnounceNothingNew,
} from "./changelog-generation-status"
import { ChangelogTagSelect } from "./changelog-tag-select"
import { EditChangelogTagDialog } from "./edit-changelog-tag-dialog"
import { CoverUploadBox, GeneratedCoverButton } from "./generated-cover-button"
import { ShippedSinceCard } from "./shipped-since-card"

function isEditableElement(el: Element | null): boolean {
	if (!el) return false
	const tag = el.tagName
	if (tag === "INPUT" || tag === "TEXTAREA") return true
	if (el.getAttribute("contenteditable") === "true") return true
	if (el.closest(".ProseMirror")) return true
	return false
}

interface ChangelogCoverUpload {
	withBackground: boolean
	successTitle: string
	event: "upload-changelog-image" | "remove-changelog-background"
}

interface ChangelogEntryProps {
	changelog: Changelog & { translations?: readonly ChangelogTranslation[] }
	// Lets the parent screen read the live editor for the copilot's page
	// context. Pass a stable callback so the memo boundary holds.
	registerEditorGetter?: (getter: () => Editor | null) => void
}

export const ChangelogEntry = memo(
	forwardRef<HTMLDivElement, ChangelogEntryProps>(
		({ changelog, registerEditorGetter }, ref) => {
			const { role } = useRole()
			const navigate = useNavigate()
			const zero = useZero()
			const decodedToken = useAuthData()
			const workspace = useZeroWorkspace()
			const { selectedLanguage } = useChangelogLanguage()

			const { passes: hasScalePlan } = useRequiredPlans(scale.andUp)

			const [linkedTags] = useQuery(
				queries.changelogTagsForChangelog(changelog.id),
			)
			const linkedTagIds = linkedTags.map((t) => t.id)
			const [editingTag, setEditingTag] = useState<
				(typeof linkedTags)[number] | null
			>(null)

			const { selectedTranslation, defaultLanguage } = useChangelogTranslations(
				changelog,
				selectedLanguage,
			)

			// Lock the editor for this language while a background translation runs.
			const isTranslationInFlight = useIsTranslationRunning(selectedTranslation)

			const session = useAuthStateData()
			const sharedDoc = useSharedDoc({
				name: collabDocName("changelog", changelog.id, selectedLanguage.code),
				token: session?.liveToken,
				enabled: !isTranslationInFlight,
			})
			const sharedYdoc = sharedDoc.status === "ready" ? sharedDoc.ydoc : null
			const sharedYdocRef = useRef<Y.Doc | null>(null)
			sharedYdocRef.current = sharedYdoc

			const [content, setContent] = useState(
				selectedTranslation.notes ?? changelog.notes,
			)
			const [title, setTitle] = useState(
				selectedTranslation.title ?? changelog.title,
			)
			const [imageUrl, setImageUrl] = useState(
				selectedTranslation.imageUrl ?? changelog.imageUrl,
			)
			const [coverVideoUrl, setCoverVideoUrl] = useState(
				() => coverFor(selectedTranslation, changelog).coverVideoUrl,
			)

			const [lastSavedVersion, setLastSavedVersion] = useState<{
				title: string
				notes: typeof changelog.notes
				imageUrl: string | null
			}>({
				title: changelog.title,
				notes: changelog.notes,
				imageUrl: changelog.imageUrl,
			})
			const [isEditorEmpty, setIsEditorEmpty] = useState(() =>
				isContentEmpty(selectedTranslation.notes ?? changelog.notes),
			)

			// Track the last synced notes to prevent re-syncing the same content
			// This breaks the loop: sync -> save -> Zero sync -> effect runs -> sync again
			const lastSyncedNotesRef = useRef<string | null>(null)

			// Track whether we need to sync AI-generated content
			// This flag persists until we successfully sync, unlike aiJustCompleted which is only true for one render
			const [needsAiContentSync, setNeedsAiContentSync] = useState(false)

			// Set the sync flag when AI generation starts (QUEUED or UPDATING status)
			useEffect(() => {
				if (
					changelog.changelogStatus === "QUEUED" ||
					changelog.changelogStatus === "UPDATING"
				) {
					setNeedsAiContentSync(true)
				}
			}, [changelog.changelogStatus])

			// Check if AI generation is completed and we still need to sync
			const shouldSyncAiContent =
				needsAiContentSync && changelog.changelogStatus === "COMPLETED"

			const { isDirty, setIsDirty, setIsSaving, setOnSave } =
				useChangelogContext()

			const [autosaveGate] = useState(createAutosaveGate)

			// Saves await the SERVER result, so a rejected save never looks saved.
			// Report non-conflict rejections once per editor session.
			const hasReportedRejection = useRef(false)
			const reportSaveRejected = useCallback(
				(message: string) => {
					if (hasReportedRejection.current) return
					hasReportedRejection.current = true
					captureError(new Error(message), {
						domain: "general",
						action: "changelog save rejected by server",
						workspaceId: decodedToken.workspaceId,
						userId: decodedToken.userId,
						extra: { changelogId: changelog.id },
					})
					toast.error({
						title: "Your changelog edits didn't save",
						description:
							"Reload the page to get the newest version, then apply your change again.",
						icon: StackPerspectiveIcon,
					})
				},
				[changelog.id, decodedToken],
			)

			// Base-row version the editor content was loaded from. Sent with every
			// save as a compare-and-set; adopted again whenever backend content is
			// pulled into the editor.
			const baseVersionRef = useRef(changelog.version)
			// While a conflict is unresolved every save is blocked: retrying would
			// keep overwriting the other person's newer content.
			const [hasConflict, setHasConflict] = useState(false)
			const hasConflictRef = useRef(false)

			// Latest render values, for callbacks that outlive their closure
			// (conflict toast action, post-await dirty check, unmount flush).
			const draftRef = useRef({ content, title, imageUrl, coverVideoUrl })
			const changelogRef = useRef(changelog)
			const selectedTranslationRef = useRef(selectedTranslation)
			const languageRef = useRef(selectedLanguage.code)
			const isDirtyRef = useRef(isDirty)
			useEffect(() => {
				draftRef.current = { content, title, imageUrl, coverVideoUrl }
				changelogRef.current = changelog
				selectedTranslationRef.current = selectedTranslation
				languageRef.current = selectedLanguage.code
				isDirtyRef.current = isDirty
			})

			const resolveConflict = useCallback(() => {
				const latest = changelogRef.current
				const translation = selectedTranslationRef.current
				const incoming =
					languageRef.current === defaultLanguage
						? latest.notes
						: (translation.notes ?? latest.notes)
				const editor = tiptapRef.current?.getEditorInstance()
				if (editor && typeof incoming === "object" && incoming !== null) {
					editor.commands.setContent(
						incoming as readonly ReadonlyJSONValue[] | ReadonlyJSONObject,
					)
					lastSyncedNotesRef.current = JSON.stringify(incoming)
					setContent(incoming)
					setIsEditorEmpty(editor.isEmpty)
				}
				setTitle(translation.title ?? latest.title)
				setImageUrl(translation.imageUrl ?? latest.imageUrl)
				setCoverVideoUrl(coverFor(translation, latest).coverVideoUrl)
				baseVersionRef.current = latest.version
				hasConflictRef.current = false
				setHasConflict(false)
				setIsDirty(false)
			}, [defaultLanguage, setIsDirty])

			const handleSaveConflict = useCallback(() => {
				if (hasConflictRef.current) return
				hasConflictRef.current = true
				setHasConflict(true)
				// Park the local text in the version history so resolving the
				// conflict cannot lose it.
				const draft = draftRef.current
				void zero.mutate(
					mutators.changelogs.createVersion({
						id: generateId(),
						changelogId: changelog.id,
						title: draft.title,
						notes: JSON.parse(JSON.stringify(draft.content)) as JSONValue,
						imageUrl: draft.imageUrl,
						coverVideoUrl: draft.coverVideoUrl,
						language: languageRef.current,
						forceNewVersion: true,
					}),
				).client
				toast.info({
					title: "Someone else saved this changelog while you were editing",
					description:
						"Your text is kept in the version history. Load the newest version to continue editing.",
					icon: StackPerspectiveIcon,
					time: 30_000,
					action: {
						label: "Load newest version",
						callback: resolveConflict,
					},
				})
			}, [changelog.id, zero, resolveConflict])

			// One id per changelog + language while the row is still in flight, so two
			// writes never insert duplicate translation rows for the same language.
			const newTranslationIdsRef = useRef<Record<string, string>>({})
			const resolveTranslationId = useCallback(
				() =>
					pickTranslationRowId({
						savedId: selectedTranslation.id,
						cache: newTranslationIdsRef.current,
						cacheKey: `${changelog.id}:${selectedLanguage.code}`,
						generateId,
					}),
				[selectedTranslation.id, selectedLanguage.code, changelog.id],
			)

			const { passes: hasRequiredPlan } = useRequiredPlans(pro.andUp)

			const doSave = useCallback(
				async (source: AutosaveSource = "USER") => {
					if (hasConflictRef.current) return
					setIsSaving(true)
					const draftAtStart = { content, title, imageUrl, coverVideoUrl }
					try {
						const translationId = resolveTranslationId()
						const updateBaseRow = selectedLanguage.code === defaultLanguage
						// The AI catch-up save copies content the server just wrote, so
						// it carries no user work worth guarding; the job's own status
						// writes would race the check into false conflicts.
						// In a live session every editor shares one merged doc, so another
						// editor's save is not a conflict to guard against.
						const expectedVersion =
							updateBaseRow && source !== "AI" && !sharedYdocRef.current
								? baseVersionRef.current
								: null
						if (sharedYdocRef.current) {
							rememberSaved(sharedYdocRef.current, content as Content)
						}
						const wantsSnapshot = shouldCreateAutoVersion({
							currentTitle: title,
							currentContent: content,
							currentImageUrl: imageUrl,
							lastSavedVersion,
						})

						await mutateOk(
							zero.mutate(
								mutators.changelogs.saveContent({
									changelogId: changelog.id,
									translationId,
									language: selectedLanguage.code,
									title,
									notes: JSON.parse(JSON.stringify(content)) as JSONValue,
									imageUrl,
									coverVideoUrl,
									updateBaseRow,
									expectedVersion,
									snapshot: wantsSnapshot
										? { id: generateId(), createdBySource: source }
										: null,
								}),
							),
							"server",
						)

						if (expectedVersion !== null) {
							baseVersionRef.current = expectedVersion + 1
						}
						if (wantsSnapshot) {
							setLastSavedVersion({ title, notes: content, imageUrl })
						}
						analytics.track("update-changelog", { id: changelog.id })

						// Edits made while the save was in flight stay dirty; the
						// debounce fires again for them.
						const latest = draftRef.current
						if (
							latest.content === draftAtStart.content &&
							latest.title === draftAtStart.title &&
							latest.imageUrl === draftAtStart.imageUrl &&
							latest.coverVideoUrl === draftAtStart.coverVideoUrl
						) {
							setIsDirty(false)
						}
					} catch (error) {
						if (
							isChangelogSaveConflict(
								error instanceof Error ? error.message : error,
							)
						) {
							handleSaveConflict()
						} else {
							console.error("updating changelog failed", error)
							captureError(error, {
								domain: "general",
								action: "update changelog",
								workspaceId: decodedToken.workspaceId,
								userId: decodedToken.userId,
								extra: { changelogId: changelog.id },
							})
							reportSaveRejected(
								error instanceof Error ? error.message : "save failed",
							)
						}
					}
					setIsSaving(false)
				},
				[
					lastSavedVersion,
					setIsSaving,
					setIsDirty,
					changelog.id,
					imageUrl,
					coverVideoUrl,
					content,
					title,
					resolveTranslationId,
					selectedLanguage.code,
					defaultLanguage,
					decodedToken,
					zero,
					reportSaveRejected,
					handleSaveConflict,
				],
			)

			// Queued re-runs read the then-latest doSave through the ref.
			const doSaveRef = useRef(doSave)
			useEffect(() => {
				doSaveRef.current = doSave
			}, [doSave])
			const [save] = useState(() =>
				createSaveQueue((source) => doSaveRef.current(source)),
			)

			// Initialize isEditorEmpty state when editor is ready
			useEffect(() => {
				const editor = tiptapRef.current?.getEditorInstance()
				if (editor) {
					setIsEditorEmpty(editor.isEmpty)
				}
			}, [])

			// Track previous language to detect actual language changes
			const previousLanguageRef = useRef(selectedLanguage.code)

			// Update title, content, and imageUrl when language changes
			useEffect(() => {
				const languageActuallyChanged =
					previousLanguageRef.current !== selectedLanguage.code
				previousLanguageRef.current = selectedLanguage.code

				// The conflicted text is already parked in the version history; the
				// new language shows a different document.
				if (languageActuallyChanged) {
					hasConflictRef.current = false
					setHasConflict(false)
				}

				if (
					!shouldApplyTranslationToEditor({
						languageChanged: languageActuallyChanged,
						isDirty,
					})
				) {
					return
				}

				baseVersionRef.current = changelog.version

				const newTitle = selectedTranslation.title ?? changelog.title
				setTitle(newTitle)

				const newImageUrl = selectedTranslation.imageUrl ?? changelog.imageUrl
				setImageUrl(newImageUrl)
				setCoverVideoUrl(coverFor(selectedTranslation, changelog).coverVideoUrl)

				const newContent = selectedTranslation.notes ?? changelog.notes
				setContent(newContent)

				// Reset image-related state when language changes
				setImagePreview(null)
				setOriginalImageFile(null)
				setIsBackgroundRemoved(false)

				// Only reset AI sync flag when language ACTUALLY changes, not when translation ID changes
				// (translation ID can change for new changelogs without saved translations)
				if (languageActuallyChanged) {
					setNeedsAiContentSync(false)
				}

				// A row landing for the text already on screen must not replace the
				// doc: the replace is an undo step nobody sees and moves the caret.
				// A live doc for the new language is seeded by the remounted editor.
				const editor = tiptapRef.current?.getEditorInstance()
				if (
					editor &&
					!editor.isDestroyed &&
					!sharedYdocRef.current &&
					!isSameEditorDoc(newContent, editor.getJSON())
				) {
					editor.commands.setContent(
						newContent as readonly ReadonlyJSONValue[] | ReadonlyJSONObject,
					)
				}
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [selectedLanguage.code, selectedTranslation.id])

			// Pull a freshly written non-default translation (e.g. an AI translation
			// that overwrote existing content) into the editor without a language
			// switch. The default-language path is already handled above.
			useEffect(() => {
				if (selectedLanguage.code === defaultLanguage) return
				// While a background translation is QUEUED/TRANSLATING the row holds an
				// empty placeholder; don't pull it into the editor or it wipes the page.
				// Content syncs once the job marks the row COMPLETED.
				if (isTranslationInFlight) return
				// Only unsaved local edits hold the incoming content back; a merely
				// focused editor with nothing to lose used to keep the stale text.
				const editor = tiptapRef.current?.getEditorInstance()
				if (!editor || isDirty) return

				const incoming = selectedTranslation.notes ?? changelog.notes
				if (isSameEditorDoc(incoming, editor.getJSON())) return
				if (!acceptsOutsideEdit(sharedDoc, incoming as Content)) return

				editor.commands.setContent(
					incoming as readonly ReadonlyJSONValue[] | ReadonlyJSONObject,
				)
				setContent(incoming)
				setTitle(selectedTranslation.title ?? changelog.title)
				setImageUrl(selectedTranslation.imageUrl ?? changelog.imageUrl)
				setCoverVideoUrl(coverFor(selectedTranslation, changelog).coverVideoUrl)
				setIsEditorEmpty(editor.isEmpty)
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [
				selectedTranslation.notes,
				selectedTranslation.title,
				selectedTranslation.imageUrl,
				isTranslationInFlight,
				selectedLanguage.code,
				defaultLanguage,
				isDirty,
				sharedDoc,
			])

			// Read through a ref so registering the handler does not re-run on every
			// edit: `save` changes identity with the content, and the reset below
			// would then clear isDirty between an edit and its autosave, leaving the
			// backend sync effects free to overwrite it.
			const saveRef = useRef(save)
			useEffect(() => {
				saveRef.current = save
			}, [save])

			useEffect(() => {
				if (!changelog.published) {
					setIsDirty(false)
					setOnSave(null)
					return
				}

				// @ts-expect-error setState updater form, not a state value
				setOnSave(() => () => saveRef.current())
				return () => setOnSave(null)
			}, [changelog.published, setIsDirty, setOnSave])

			useEffect(() => {
				const handleBeforeUnload = (event: BeforeUnloadEvent) => {
					if (isDirtyRef.current) event.preventDefault()
				}
				window.addEventListener("beforeunload", handleBeforeUnload)
				return () =>
					window.removeEventListener("beforeunload", handleBeforeUnload)
			}, [])

			// The 200ms autosave debounce dies with the component; edits made right
			// before switching changelogs or closing the pane flush here.
			useEffect(() => {
				return () => {
					const source = autosaveGate.take()
					if (source || isDirtyRef.current) {
						void saveRef.current(source ?? "USER")
					}
				}
			}, [autosaveGate])

			const blocker = useBlocker({
				shouldBlockFn: ({ current, next }) =>
					isDirty && current.pathname !== next.pathname,
				withResolver: true,
				enableBeforeUnload: false,
			})

			// Auto-save on navigation instead of prompting. Run once per blocked
			// navigation: save() re-renders, which re-fires this effect and would
			// re-enter save() in a loop that freezes the page. The ref gates the
			// effect; the race caps how long a stalled sync connection can hold
			// navigation (the mutation is already queued locally).
			const blockerHandledRef = useRef(false)
			// The blocker can reset while the save runs; proceeding then throws.
			const blockerRef = useRef(blocker)
			blockerRef.current = blocker
			useEffect(() => {
				if (blocker.status !== "blocked") {
					blockerHandledRef.current = false
					return
				}
				if (blockerHandledRef.current) return
				blockerHandledRef.current = true
				const timeout = new Promise((resolve) => setTimeout(resolve, 2500))
				void Promise.race([saveRef.current(), timeout]).finally(() => {
					if (blockerRef.current.status === "blocked") {
						blockerRef.current.proceed()
					}
				})
			}, [blocker])

			// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-assignment
			const [imagePreview, setImagePreview] = useState<any>(null)
			const [originalImageFile, setOriginalImageFile] = useState<File | null>(
				null,
			)
			const [isBackgroundRemoved, setIsBackgroundRemoved] = useState(false)
			const [isImageBusy, setIsImageBusy] = useState(false)
			const imageInput = useRef<HTMLInputElement | null>(null)
			const thumbnailInput = useRef<HTMLInputElement | null>(null)
			const getSignedUploadUrlMutation = api.file.getUrl.useMutation()

			const uploadCover = useCallback(
				async (
					file: File,
					{ withBackground, successTitle, event }: ChangelogCoverUpload,
				) => {
					setIsImageBusy(true)
					try {
						const fileToUpload = withBackground
							? await processCoverImage(file)
							: file

						setImagePreview(URL.createObjectURL(fileToUpload))

						const { uploadUrl, fileUrl } =
							await getSignedUploadUrlMutation.mutateAsync({
								fileName: fileToUpload.name,
								fileType: fileToUpload.type,
							})
						const filesize = (fileToUpload.size / 1024 / 1024).toFixed(4)
						if (Number(filesize) < ALLOWED_FILE_SIZE_IN_MB) {
							const headers = { "Content-Type": fileToUpload.type }
							const res = await putWithRetry(uploadUrl, {
								method: "PUT",
								body: fileToUpload,
								headers,
							})
							if (!res.ok) {
								throw new Error("File upload failed")
							}
							// Persist through the guarded save path so an image change
							// cannot overwrite a newer version either.
							setImageUrl(fileUrl)
							setCoverVideoUrl(null)
							autosaveGate.queue()
							setIsDirty(true)
							setIsBackgroundRemoved(!withBackground)
							toast.success({ title: successTitle })
							analytics.track(event, { id: changelog.id })
						} else {
							toast.error({
								title: `Images need to be in jpg or png format and less than ${ALLOWED_FILE_SIZE_IN_MB}mb in size.`,
							})
						}
					} catch (error) {
						toast.error({ title: "Something went wrong" })
						console.error("Image processing/upload error:", error)
						// The visitor's own network, not a bug; the toast already told them.
						if (!isFetchNetworkError(error)) {
							captureError(error, {
								domain: "general",
								action: "upload changelog image",
								workspaceId: decodedToken.workspaceId,
								userId: decodedToken.userId,
								extra: { changelogId: changelog.id },
							})
						}
					}
					setIsImageBusy(false)
				},
				[
					changelog.id,
					getSignedUploadUrlMutation,
					autosaveGate,
					setIsDirty,
					decodedToken,
				],
			)

			const uploadFile = useCallback(
				async (file: File) => {
					const { uploadUrl, fileUrl } =
						await getSignedUploadUrlMutation.mutateAsync({
							fileName: file.name,
							fileType: file.type,
						})
					const res = await putWithRetry(uploadUrl, {
						method: "PUT",
						body: file,
						headers: { "Content-Type": file.type },
					})
					if (!res.ok) throw new Error("File upload failed")
					return fileUrl
				},
				[getSignedUploadUrlMutation],
			)

			// The still doubles as the poster, the thumbnail in lists and emails,
			// and the og:image, so everything that reads imageUrl keeps working.
			const uploadCoverVideo = useCallback(
				async (file: File) => {
					if (file.size / 1024 / 1024 >= MAX_COVER_VIDEO_MB) {
						toast.error({
							title: `Cover videos can be at most ${MAX_COVER_VIDEO_MB}mb. Upload a shorter or smaller file.`,
						})
						return
					}
					setIsImageBusy(true)
					try {
						const poster = await captureVideoPoster(file)
						setImagePreview(URL.createObjectURL(poster))
						const [posterUrl, videoUrl] = await Promise.all([
							uploadFile(poster),
							uploadFile(file),
						])
						setImageUrl(posterUrl)
						setCoverVideoUrl(videoUrl)
						setImagePreview(null)
						setOriginalImageFile(null)
						autosaveGate.queue()
						setIsDirty(true)
						toast.success({ title: "Video uploaded" })
						analytics.track("upload-changelog-video", { id: changelog.id })
					} catch (error) {
						setImagePreview(null)
						toast.error({
							title: "This video couldn't be uploaded. Try an mp4 file.",
						})
						if (!isFetchNetworkError(error)) {
							captureError(error, {
								domain: "general",
								action: "upload changelog video",
								workspaceId: decodedToken.workspaceId,
								userId: decodedToken.userId,
								extra: { changelogId: changelog.id },
							})
						}
					}
					setIsImageBusy(false)
				},
				[uploadFile, autosaveGate, setIsDirty, changelog.id, decodedToken],
			)

			// Swaps only the still: the video stays, and the new image becomes the
			// poster, list thumbnail, email image and og:image.
			const uploadVideoThumbnail = useCallback(
				async (file: File) => {
					if (file.size / 1024 / 1024 >= ALLOWED_FILE_SIZE_IN_MB) {
						toast.error({
							title: `Thumbnails can be at most ${ALLOWED_FILE_SIZE_IN_MB}mb. Upload a smaller image.`,
						})
						return
					}
					setIsImageBusy(true)
					try {
						const url = await uploadFile(file)
						setImageUrl(url)
						autosaveGate.queue()
						setIsDirty(true)
						toast.success({ title: "Thumbnail uploaded" })
						analytics.track("upload-changelog-video-thumbnail", {
							id: changelog.id,
						})
					} catch (error) {
						toast.error({ title: "This thumbnail couldn't be uploaded" })
						if (!isFetchNetworkError(error)) {
							captureError(error, {
								domain: "general",
								action: "upload changelog video thumbnail",
								workspaceId: decodedToken.workspaceId,
								userId: decodedToken.userId,
								extra: { changelogId: changelog.id },
							})
						}
					}
					setIsImageBusy(false)
					if (thumbnailInput.current) thumbnailInput.current.value = ""
				},
				[uploadFile, autosaveGate, setIsDirty, changelog.id, decodedToken],
			)

			// A new cover keeps its own pixels; the padded plate is opt-in.
			const onImageChange = useCallback(
				async (f: File) => {
					if (isVideoFile(f)) {
						await uploadCoverVideo(f)
						if (imageInput.current) imageInput.current.value = ""
						return
					}
					setOriginalImageFile(f)
					await uploadCover(f, {
						withBackground: false,
						successTitle: "Image uploaded",
						event: "upload-changelog-image",
					})
					if (imageInput.current) {
						imageInput.current.value = ""
					}
				},
				[uploadCover, uploadCoverVideo],
			)

			const addBackground = (file: File) =>
				uploadCover(file, {
					withBackground: true,
					successTitle: "Image uploaded",
					event: "upload-changelog-image",
				})

			const removeBackground = (file: File) =>
				uploadCover(file, {
					withBackground: false,
					successTitle: "Background removed, using original image",
					event: "remove-changelog-background",
				})

			// null drops an uploaded cover back to the generated one; HIDDEN_CHANGELOG_COVER removes that too.
			const clearCover = async (next: string | null) => {
				if (role === "VIEWER") {
					return
				}
				autosaveGate.queue()
				setIsDirty(true)
				setImageUrl(next)
				setCoverVideoUrl(null)
				setImagePreview(null)
				setOriginalImageFile(null)
				setIsBackgroundRemoved(false)
				const translationId = resolveTranslationId()
				if (workspace) {
					await mutateOk(
						zero.mutate(
							mutators.changelogTranslations.upsert({
								id: translationId,
								workspaceId: workspace.id,
								language: selectedLanguage.code,
								changelogId: changelog.id,
								title: title,
								notes: content,
								imageUrl: next,
								coverVideoUrl: null,
							}),
						),
					)
				}
				// Also update main changelog if default language
				if (selectedLanguage.code === defaultLanguage) {
					await mutateOk(
						zero.mutate(
							mutators.changelogs.update({
								id: changelog.id,
								imageUrl: next,
								coverVideoUrl: null,
							}),
						),
					)
				}
				analytics.track("unset-changelog-cover", {
					id: changelog.id,
					generated: next === HIDDEN_CHANGELOG_COVER,
				})
			}

			const triggerFileSelection = useCallback(() => {
				if (role === "VIEWER") {
					return
				}
				imageInput.current?.click()
			}, [role])

			useEffect(() => {
				const handlePaste = (e: ClipboardEvent) => {
					if (role === "VIEWER") return
					if (isEditableElement(document.activeElement)) return
					if (isImageBusy) return

					const image = getClipboardFiles(e.clipboardData?.items).find((file) =>
						file.type.startsWith("image"),
					)
					if (image) {
						e.preventDefault()
						// On a video cover a pasted image is its new thumbnail, not a
						// replacement that would drop the video.
						void (coverVideoUrl
							? uploadVideoThumbnail(image)
							: onImageChange(image))
					}
				}

				document.addEventListener("paste", handlePaste)
				return () => document.removeEventListener("paste", handlePaste)
			}, [
				role,
				isImageBusy,
				onImageChange,
				coverVideoUrl,
				uploadVideoThumbnail,
			])

			useDebounce(
				async () => {
					const source = autosaveGate.take()
					if (!source) return
					await save(source)
				},
				200,
				[content, title, imageUrl, coverVideoUrl],
			)

			// Kept so "Try again" reruns the same picked release.
			const lastSelectionRef = useRef<ChangelogSelection | undefined>(undefined)

			const getCompletedIssuesMutation =
				api.linear.getCompletedIssuesForChangelog.useMutation()

			// Determine if AI generation is in progress based on changelogStatus
			const isGenerating = Boolean(
				changelog.changelogStatus &&
				changelog.changelogStatus !== "COMPLETED" &&
				changelog.changelogStatus !== "FAILED",
			)

			// Get user-friendly status message from database status
			const statusMessage =
				changelog.changelogStatus &&
				changelog.changelogStatus in CHANGELOG_STATUS_MESSAGES
					? CHANGELOG_STATUS_MESSAGES[
							changelog.changelogStatus as keyof typeof CHANGELOG_STATUS_MESSAGES
						]
					: ""
			const editorPlaceholder = isGenerating
				? statusMessage
				: "Start writing, or pick what was released below..."

			const startFailedRef = useRef(false)
			const autoInsertLinearIssues = useCallback(
				async (selection?: ChangelogSelection) => {
					lastSelectionRef.current = selection
					if (!hasLinearConnection(workspace)) {
						toast.info({
							title: "Linear is not connected",
							descriptionAction: {
								label: "Click here to connect Linear in the settings",
								callback: () => {
									void navigate({ to: "/settings/integrations/linear" })
								},
							},
						})
						return
					}

					if (!hasRequiredPlan) {
						toast.info({
							title: "Release intelligence is available on Pro and Scale plans",
							icon: CircleArrowUp,
							action: {
								label: "View Plans",
								callback: () => {
									void navigate({ to: "/settings/plans" })
								},
							},
						})
						return
					}

					if (workspace.remainingAiChangelogGenerations <= 0) {
						toast.error({
							title: "AI Changelog Quota Exceeded",
							description:
								"You've reached your daily AI changelog generation limit (20 per day). Try again tomorrow.",
						})
						return
					}

					// Set status to QUEUED immediately to show loading state
					const result = await zero.mutate(
						mutators.changelogs.update({
							id: changelog.id,
							changelogStatus: "QUEUED",
						}),
					).client

					if (result.type === "error") {
						console.error("Auto-insert Linear issues failed", result.error)
						captureError(result.error, {
							domain: "linear",
							action: "auto-insert linear issues",
							workspaceId: decodedToken.workspaceId,
							userId: decodedToken.userId,
							extra: { changelogId: changelog.id },
						})
						toast.error({
							id: `changelog-start-failed-${changelog.id}`,
							title: "Couldn't start writing the changelog",
							description: "Your entry is unchanged. Try again in a moment.",
							icon: StackPerspectiveFillIcon,
						})
						return
					}

					try {
						// Trigger the background AI generation
						// The status will be updated in real-time via zero from the database
						await getCompletedIssuesMutation.mutateAsync({
							changelogId: changelog.id,
							selection,
						})

						analytics.track("auto-insert-linear-issues-to-changelog", {
							id: changelog.id,
							picked: !!selection,
						})
					} catch (error) {
						const budgetReached =
							error instanceof Error &&
							error.message === AI_BUDGET_REACHED_MESSAGE

						if (budgetReached) {
							toast.info({
								id: "changelog-ai-budget-reached",
								title: AI_BUDGET_REACHED_MESSAGE,
								icon: CreditCardIcon,
								action: {
									label: "Open AI usage",
									callback: () => {
										void navigate({ to: "/settings/ai/usage" })
									},
								},
							})
						} else {
							console.error("Auto-insert Linear issues failed", error)
							captureError(error, {
								domain: "linear",
								action: "auto-insert linear issues",
								workspaceId: decodedToken.workspaceId,
								userId: decodedToken.userId,
								extra: { changelogId: changelog.id },
							})
							toast.error({
								id: `changelog-start-failed-${changelog.id}`,
								title: "Couldn't start writing the changelog",
								description: "Your entry is unchanged. Try again in a moment.",
								icon: StackPerspectiveFillIcon,
								action: {
									label: "Try again",
									callback: () => void autoInsertLinearIssues(selection),
								},
							})
						}

						// Reset status on error
						startFailedRef.current = true
						await mutateOk(
							zero.mutate(
								mutators.changelogs.update({
									id: changelog.id,
									changelogStatus: null,
								}),
							),
						)
					}
				},
				[
					workspace,
					getCompletedIssuesMutation,
					changelog.id,
					navigate,
					hasRequiredPlan,
					decodedToken,
					zero,
				],
			)

			const generateFromSelection = useCallback(
				(selection: ChangelogSelection) =>
					void autoInsertLinearIssues(selection),
				[autoInsertLinearIssues],
			)

			// Listen for auto-generate AI event from empty state
			useEffect(() => {
				const handleAutoGenerate = (
					event: CustomEvent<{ changelogId: string }>,
				) => {
					if (event.detail?.changelogId === changelog.id) {
						// Trigger AI generation automatically
						void autoInsertLinearIssues()
					}
				}

				window.addEventListener(
					"changelog-generate-ai",
					handleAutoGenerate as EventListener,
				)
				return () => {
					window.removeEventListener(
						"changelog-generate-ai",
						handleAutoGenerate as EventListener,
					)
				}
			}, [changelog.id, autoInsertLinearIssues])

			// A background generation that fails leaves the editor untouched, so the
			// stopped spinner is the only signal the user would otherwise get.
			const lastStatusRef = useRef(changelog.changelogStatus)
			useEffect(() => {
				const previous = lastStatusRef.current
				lastStatusRef.current = changelog.changelogStatus
				if (shouldAnnounceNothingNew(previous, changelog.changelogStatus)) {
					if (startFailedRef.current) {
						startFailedRef.current = false
						return
					}
					toast.info({
						id: `changelog-nothing-new-${changelog.id}`,
						title: "Nothing new since your last changelog",
						description:
							"No completed issues, projects, or merged pull requests since then. Your entry was left unchanged.",
						icon: StackPerspectiveFillIcon,
					})
					return
				}
				if (
					!shouldAnnounceGenerationFailure(previous, changelog.changelogStatus)
				) {
					return
				}

				toast.error({
					id: `changelog-generation-failed-${changelog.id}`,
					title: "Changelog generation failed",
					description:
						"Your entry was left unchanged. Try again, or check your AI usage in settings.",
					icon: StackPerspectiveFillIcon,
					action: {
						label: "Try again",
						callback: () =>
							void autoInsertLinearIssues(lastSelectionRef.current),
					},
				})
			}, [changelog.changelogStatus, changelog.id, autoInsertLinearIssues])

			const tiptapRef = useRef<{ getEditorInstance: () => Editor } | null>(null)
			const presenceRef = useRef<{ docKey: DocPresenceKey; userId: string }>({
				docKey: `changelog:${changelog.id}:${selectedLanguage.code}`,
				userId: decodedToken.userId,
			})
			presenceRef.current = {
				docKey: `changelog:${changelog.id}:${selectedLanguage.code}`,
				userId: decodedToken.userId,
			}
			const colorSeed = useColorSeedLookup()
			const [cursorExtensions] = useState(() => [
				RemoteCursors.configure({
					getColorSeed: colorSeed,
					getDocKey: () => presenceRef.current.docKey,
					getSelfUserId: () => presenceRef.current.userId,
				}),
			])
			const [isTitleFocused, setIsTitleFocused] = useState(false)

			// Expose the live editor to the parent screen (copilot page context).
			useEffect(() => {
				registerEditorGetter?.(
					() => tiptapRef.current?.getEditorInstance() ?? null,
				)
			}, [registerEditorGetter])

			// Copilot "Apply": replace the changelog body with the proposed content
			// so the user previews the change in place. Saving stays separate.
			const pendingContentRewrite = useSidebarAiChatState(
				(s) => s.pendingContentRewrite,
			)
			const clearPendingContentRewrite = useSidebarAiChatState(
				(s) => s.setPendingContentRewrite,
			)
			useEffect(() => {
				if (pendingContentRewrite?.target !== "changelog") return
				const editor = tiptapRef.current?.getEditorInstance()
				if (!editor) return
				editor.commands.setContent(pendingContentRewrite.html)
				autosaveGate.queue()
				setIsDirty(true)
				setContent(editor.getJSON())
				setIsEditorEmpty(editor.isEmpty)
				clearPendingContentRewrite(null)
			}, [
				pendingContentRewrite,
				clearPendingContentRewrite,
				setIsDirty,
				autosaveGate,
			])

			// Update editor content when changelog.notes changes from the backend
			// Only sync from changelog.notes when viewing the default language
			// For non-default languages, content comes from translations
			useEffect(() => {
				const editor = tiptapRef.current?.getEditorInstance()
				if (
					editor &&
					changelog.notes &&
					selectedLanguage.code === defaultLanguage
				) {
					const newContent = changelog.notes
					const newContentStr = JSON.stringify(newContent)

					// Version moves without a content change too (status writes bump
					// it). A clean editor follows it, or the next compare-and-set
					// fails against a row whose content it already shows.
					if (
						!isDirty &&
						!hasConflictRef.current &&
						lastSyncedNotesRef.current === newContentStr
					) {
						baseVersionRef.current = changelog.version
					}

					// Skip if we already synced this exact content (prevents sync loops)
					if (
						lastSyncedNotesRef.current === newContentStr &&
						!shouldSyncAiContent
					) {
						return
					}

					const contentChanged = !isSameEditorDoc(newContent, editor.getJSON())

					if (sharedDoc.status === "ready") {
						baseVersionRef.current = changelog.version
						if (!contentChanged) {
							if (shouldSyncAiContent) setNeedsAiContentSync(false)
							return
						}
						if (
							typeof newContent !== "object" ||
							newContent === null ||
							(isDirty && !shouldSyncAiContent)
						) {
							return
						}
						lastSyncedNotesRef.current = newContentStr
						if (!acceptsOutsideEdit(sharedDoc, newContent as Content)) {
							if (shouldSyncAiContent) setNeedsAiContentSync(false)
							return
						}
						editor.commands.setContent(newContent)
						setContent(newContent)
						setIsEditorEmpty(editor.isEmpty)
						setLastSavedVersion({
							title: changelog.title,
							notes: changelog.notes,
							imageUrl: changelog.imageUrl,
						})
						if (shouldSyncAiContent) {
							autosaveGate.queue("AI")
							setNeedsAiContentSync(false)
							setIsDirty(false)
						}
						return
					}

					// The editor already shows this row (our own save echoing back, or
					// an identical remote save): adopt its version without a rewrite.
					if (!contentChanged && !isDirty && !hasConflictRef.current) {
						baseVersionRef.current = changelog.version
					}

					// Only update if content is different and is a valid object
					// Skip if the editor is currently focused (user is editing)
					// Skip if there are unsaved local changes (isDirty) to prevent data loss
					// BUT force sync when AI generation just completed
					if (
						typeof newContent === "object" &&
						newContent !== null &&
						(contentChanged || shouldSyncAiContent) &&
						(!editor.isFocused || shouldSyncAiContent) &&
						(!isDirty || shouldSyncAiContent)
					) {
						lastSyncedNotesRef.current = newContentStr
						editor.commands.setContent(newContent)
						setContent(newContent)
						// The editor now holds this row's content; saves are based on it.
						baseVersionRef.current = changelog.version

						setIsEditorEmpty(editor.isEmpty)
						// Reset lastSavedVersion when content is updated from backend (e.g., version restore)
						setLastSavedVersion({
							title: changelog.title,
							notes: changelog.notes,
							imageUrl: changelog.imageUrl,
						})
						// Clear the AI sync flag and reset isDirty when we successfully sync AI content
						if (shouldSyncAiContent) {
							// The AI job writes changelog.notes only; queue a save so the
							// default-language translation row (portal reads it first) catches up.
							autosaveGate.queue("AI")
							setNeedsAiContentSync(false)
							setIsDirty(false)
						}
					}
				}
			}, [
				changelog.notes,
				changelog.version,
				isDirty,
				changelog.title,
				changelog.imageUrl,
				selectedLanguage.code,
				defaultLanguage,
				shouldSyncAiContent,
				sharedDoc,
				setIsDirty,
				autosaveGate,
			])

			// Update title and imageUrl when they change from the backend
			// Skip if there are unsaved local changes (isDirty) to prevent data loss
			// Skip if the textarea is currently focused (user is editing)
			// Only sync title from changelog.title when viewing the default language
			// imageUrl is now per-language, so sync from selectedTranslation.imageUrl
			useEffect(() => {
				const isDefaultLanguage = selectedLanguage.code === defaultLanguage
				const expectedImageUrl =
					selectedTranslation.imageUrl ?? changelog.imageUrl

				const shouldSyncTitle =
					isDefaultLanguage &&
					changelog.title !== title &&
					(!isDirty || shouldSyncAiContent) &&
					!isTitleFocused
				const expectedVideoUrl = coverFor(
					selectedTranslation,
					changelog,
				).coverVideoUrl
				const shouldSyncImageUrl =
					(expectedImageUrl !== imageUrl ||
						expectedVideoUrl !== coverVideoUrl) &&
					(!isDirty || shouldSyncAiContent)

				if (shouldSyncTitle) {
					setTitle(changelog.title)
				}
				if (shouldSyncImageUrl) {
					setImageUrl(expectedImageUrl)
					setCoverVideoUrl(expectedVideoUrl)
				}
				// Reset lastSavedVersion when external changes occur (e.g., version restore)
				// Only reset if we actually updated the values
				if (shouldSyncTitle || shouldSyncImageUrl) {
					setLastSavedVersion({
						title: changelog.title,
						notes: changelog.notes,
						imageUrl: changelog.imageUrl,
					})
				}
				// eslint-disable-next-line react-hooks/exhaustive-deps
			}, [
				changelog.title,
				isDirty,
				isTitleFocused,
				changelog.imageUrl,
				changelog.coverVideoUrl,
				selectedLanguage.code,
				defaultLanguage,
				selectedTranslation.imageUrl,
				selectedTranslation.coverVideoUrl,
				shouldSyncAiContent,
			])

			return (
				<div
					className={cn(
						"flex w-full flex-col items-center justify-center py-5 sm:py-10",
						{ "pointer-events-none select-none": isTranslationInFlight },
					)}
					ref={ref}
				>
					<EditChangelogTagDialog
						isOpen={!!editingTag}
						onOpenChange={(open) => {
							if (!open) setEditingTag(null)
						}}
						tag={editingTag}
					/>
					<div className="relative flex w-full flex-col items-center">
						{hasConflict && (
							<div className="mb-4 flex w-full max-w-[630px] items-center justify-between gap-3 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700/80 dark:bg-gray-800/50">
								<p className="text-sm">
									Someone else saved this changelog while you were editing. Your
									text is kept in the version history.
								</p>
								<Button variant="outline" size="sm" onClick={resolveConflict}>
									Load newest version
								</Button>
							</div>
						)}
						<div
							className="pointer-events-none absolute bottom-0 top-2 hidden w-3.5 lg:block"
							style={{ left: "calc(50% - 315px - 2.7rem)" }}
						>
							<ChangelogTimeline
								isFirst
								isLast
								color={
									changelog.published
										? "#9FA7B7"
										: (workspace?.accentColor ?? "#4CB782")
								}
							/>
						</div>
						<div className="mb-4 flex w-full max-w-[630px] items-center justify-end gap-2">
							<Textarea
								name="title"
								variant="borderless"
								noResize
								textSize="xxl"
								value={title}
								placeholder="New changelog"
								fontWeight="bold"
								className="no-scrollbar w-full bg-transparent font-bold"
								readOnly={role === "VIEWER"}
								onChange={(e) => {
									const target = e.target as HTMLTextAreaElement
									autosaveGate.queue()
									setTitle(target.value)
									setIsDirty(true)
								}}
								onFocus={() => setIsTitleFocused(true)}
								onBlur={() => setIsTitleFocused(false)}
								maxLength={CHANGELOG_TITLE_MAXIMUM_LENGTH}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault()
										const editor = tiptapRef.current?.getEditorInstance()
										editor?.commands.focus("start")
									}
								}}
							/>
						</div>
						<div className="mb-3 flex w-full max-w-[630px] flex-wrap items-center gap-1.5">
							{linkedTags.map((tag) => (
								<ChangelogTagBadge
									key={tag.id}
									tag={tag}
									renderIcon={({ icon, color }) => {
										if (!icon) return undefined
										const Icon = getProjectIcon(icon)
										return <Icon className="h-3 w-3" style={{ color }} />
									}}
									onEdit={
										role !== "VIEWER" ? () => setEditingTag(tag) : undefined
									}
									onRemove={
										role !== "VIEWER"
											? () =>
													void zero.mutate(
														mutators.changelogTags.unlinkTag({
															changelogId: changelog.id,
															tagId: tag.id,
														}),
													).client
											: undefined
									}
								/>
							))}
							{hasScalePlan && (
								<RoleCheck not={["VIEWER"]}>
									<ChangelogTagSelect
										changelogId={changelog.id}
										linkedTagIds={linkedTagIds}
									/>
								</RoleCheck>
							)}
						</div>
						<div className="w-full max-w-[630px] flex-1">
							{!!imageUrl || !!imagePreview ? (
								<div className="group relative">
									{coverVideoUrl && !imagePreview ? (
										<ChangelogCoverVideo
											src={coverVideoUrl}
											poster={imageUrl}
										/>
									) : (
										<ChangelogCover
											// eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
											src={imagePreview ? imagePreview : imageUrl}
											blurhash={
												imagePreview
													? null
													: coverBlurhashFor(imageUrl, [
															selectedTranslation,
															changelog,
														])
											}
										/>
									)}
									<RoleCheck not={["VIEWER"]}>
										<div className="absolute right-4 top-4 flex gap-2">
											{originalImageFile && !isBackgroundRemoved && (
												<Button
													variant="glass"
													size="lg"
													Icon={ImageMinus}
													iconStyle={{ width: "1rem", height: "1rem" }}
													className="h-9 rounded-xl bg-gray-900/20 text-white shadow-sm ring-1 ring-inset ring-white/20 backdrop-blur-lg transition-all hover:bg-gray-900/30 hover:ring-white/30 dark:bg-black/20 dark:hover:bg-black/30"
													onClick={() =>
														void removeBackground(originalImageFile)
													}
													disabled={isImageBusy}
													title="Remove background"
												>
													<span className="drop-shadow-sm">
														{isImageBusy
															? "Processing..."
															: "Remove background"}
													</span>
												</Button>
											)}
											{originalImageFile && isBackgroundRemoved && (
												<Button
													variant="glass"
													size="lg"
													Icon={ImageBackgroundIcon}
													iconStyle={{ width: "1rem", height: "1rem" }}
													className="h-9 rounded-xl bg-gray-900/20 text-white shadow-sm ring-1 ring-inset ring-white/20 backdrop-blur-lg transition-all hover:bg-gray-900/30 hover:ring-white/30 dark:bg-black/20 dark:hover:bg-black/30"
													onClick={() => void addBackground(originalImageFile)}
													disabled={isImageBusy}
													title="Add background"
												>
													<span className="drop-shadow-sm">
														{isImageBusy ? "Processing..." : "Add background"}
													</span>
												</Button>
											)}
											{coverVideoUrl && (
												<button
													className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900/20 shadow-sm ring-1 ring-inset ring-white/20 backdrop-blur-lg transition-all hover:bg-gray-900/30 hover:ring-white/30 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-black/20 dark:hover:bg-black/30"
													type="button"
													onClick={() => thumbnailInput.current?.click()}
													disabled={isImageBusy}
													title="Upload thumbnail"
													aria-label="Upload thumbnail"
												>
													<ImagePlusIcon className="h-4 w-4 flex-none text-white drop-shadow-sm" />
												</button>
											)}
											<button
												className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900/20 shadow-sm ring-1 ring-inset ring-white/20 backdrop-blur-lg transition-all hover:bg-gray-900/30 hover:ring-white/30 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-black/20 dark:hover:bg-black/30"
												type="button"
												onClick={triggerFileSelection}
												disabled={isImageBusy}
												title="Upload new image"
											>
												<UploadIcon className="h-4 w-4 text-white drop-shadow-sm" />
											</button>
											<button
												className="flex h-9 w-9 items-center justify-center rounded-xl bg-gray-900/20 shadow-sm ring-1 ring-inset ring-white/20 backdrop-blur-lg transition-all hover:bg-gray-900/30 hover:ring-white/30 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-black/20 dark:hover:bg-black/30"
												type="button"
												disabled={isImageBusy}
												onClick={() => void clearCover(null)}
												title="Delete cover image"
											>
												<TrashIcon className="h-4 w-4 text-white drop-shadow-sm" />
											</button>
										</div>
									</RoleCheck>
								</div>
							) : isChangelogCoverHidden(imageUrl) ? (
								<CoverUploadBox
									onSelect={triggerFileSelection}
									onRestore={
										role === "VIEWER" ? undefined : () => void clearCover(null)
									}
								/>
							) : (
								<GeneratedCoverButton
									src={`${defaultChangelogCoverUrl(env.VITE_API_URL, changelog.id, selectedLanguage.code)}?v=${generatedCoverVersion(changelog.date ?? changelog.createdAt, selectedTranslation.title || changelog.title)}`}
									onSelect={triggerFileSelection}
									onRemove={
										role === "VIEWER"
											? undefined
											: () => void clearCover(HIDDEN_CHANGELOG_COVER)
									}
								/>
							)}
							<input
								id="file"
								name="file"
								type="file"
								accept={COVER_FILE_ACCEPT}
								ref={imageInput}
								className="hidden"
								onChange={(e) => {
									if (e.target.files?.[0]) {
										void onImageChange(e.target.files[0])
									}
								}}
							/>
							<input
								type="file"
								accept="image/*"
								ref={thumbnailInput}
								className="hidden"
								onChange={(e) => {
									if (e.target.files?.[0]) {
										void uploadVideoThumbnail(e.target.files[0])
									}
								}}
							/>

							{sharedDoc.status === "connecting" ? (
								// The stored notes are already local; show them while the live doc joins.
								<DefaultEditor
									autofocus={false}
									initialContent={selectedTranslation.notes ?? changelog.notes}
									placeholder={editorPlaceholder}
									onUpdate={() => undefined}
									editable={false}
									className={cn("mt-8 min-h-[2rem] w-full max-w-none", {
										"[&_.ProseMirror]:pb-8": !isEditorEmpty,
									})}
									key={`pending-${changelog.id}-${selectedLanguage.code}`}
								/>
							) : (
								<DefaultEditor
									collaborative={!!sharedYdoc}
									onCreate={({ editor }) => {
										if (!sharedYdoc) return
										const seed = (selectedTranslation.notes ??
											changelog.notes) as Content
										const seedId = seedSharedDoc(
											sharedYdoc,
											editor.schema,
											seed,
										)
										if (seedId === null) return
										const stop = watchSeedRace(sharedYdoc, seedId)
										editor.on("destroy", stop)
									}}
									autofocus={false}
									initialContent={changelog.notes}
									placeholder={editorPlaceholder}
									onUpdate={({ editor, transaction }) => {
										const content = editor.getJSON()
										// A teammate's edit keeps the draft current; whoever typed it saves it.
										if (isRemoteChange(transaction)) {
											setContent(content)
											setIsEditorEmpty(editor.isEmpty)
											return
										}
										autosaveGate.queue()
										setContent(content)
										setIsDirty(true)
										setIsEditorEmpty(editor.isEmpty)
									}}
									editable={role !== "VIEWER"}
									className={cn("mt-8 min-h-[2rem] w-full max-w-none", {
										"[&_.ProseMirror]:pb-8": !isEditorEmpty,
									})}
									ref={tiptapRef}
									key={sharedYdoc ? `live-${sharedYdoc.guid}` : changelog.id}
									extensions={
										sharedDoc.status === "ready"
											? sharedDocExtensions({
													ydoc: sharedDoc.ydoc,
													provider: sharedDoc.provider,
													user: {
														userId: decodedToken.userId,
														name: session?.name || "A teammate",
														color: cursorColor(colorSeed(decodedToken.userId)),
													},
												})
											: cursorExtensions
									}
								/>
							)}

							{isEditorEmpty && (
								<RoleCheck not={["VIEWER"]}>
									{isGenerating ? (
										<div className="mt-6 flex flex-row justify-start">
											<GenerateChangelogButton
												onClick={() => undefined}
												disabled
												isLoading
												loadingText={statusMessage}
												workspaceLinearConnected={hasLinearConnection(
													workspace,
												)}
												showHowItWorks={false}
												variant="outline"
											/>
										</div>
									) : (
										<ShippedSinceCard
											canFetch={
												hasRequiredPlan && hasLinearConnection(workspace)
											}
											linearConnected={hasLinearConnection(workspace)}
											generationsLeft={
												workspace?.remainingAiChangelogGenerations ?? 0
											}
											onGenerate={generateFromSelection}
										/>
									)}
								</RoleCheck>
							)}
						</div>
					</div>
				</div>
			)
		},
	),
)
